import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { TextField, Grid, Autocomplete, List, Typography, CircularProgress } from '@mui/material';
import { AgGridUtils } from '@cosmotech/core';
import { UploadFile, UPLOAD_FILE_STATUS_KEY } from '@cosmotech/ui';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { WizardUtils } from '../../../../utils/WizardUtils';
import { useActionsImposees } from './hooks/ActionsImposeesHook';

const requiredColumns = TECHNICAL_POLICY_COLUMNS.RequiredSequencesActionsImposees;
const labels = {
  upload: {
    button: 'Importer',
    invalidFileMessage: 'Fichier invalide',
    delete: 'Supprimer',
    noFileMessage: 'Vide',
    sourceModifiee: '(source modifiée)',
    fileName: (SequencesActionsImposeesTableId) => `${SequencesActionsImposeesTableId}.csv`,
    fichierVide: 'Fichier vide',
  },
  gisements: {
    label: 'Gisements',
    noOption: 'Aucun gisement correspondant',
  },
  fileErrors: {
    missingColumn: (column) => `L'entête ${column} est manquante`,
  },
};

export const ActionsImposeesTab = (props) => {
  const {
    currentActionsImposees = {
      selectedGisement: null,
      currentFile: {
        content: null,
        file: null,
        id: null,
        name: '',
        status: UPLOAD_FILE_STATUS_KEY.EMPTY,
      },
    },
    setCurrentActionsImposees,
    gisements,
    defaultSelectedGisement = gisements[0],
    editMode,
  } = props;
  const {
    getActionImposeesDatasetId,
    getActionsImposeesTableId,
    forgeNewActionsImposeesId,
    getActionImposeesId,
    downloadFileFromStorage,
    isLoading,
    setIsLoading,
    fileError,
    setFileError,
  } = useActionsImposees();

  const { setValue, getValues } = useFormContext();

  const selectGisement = useCallback(
    (gisement) => {
      const actionImposeesTableId = getActionsImposeesTableId(gisement);
      const actionImposeesId = actionImposeesTableId ?? forgeNewActionsImposeesId(gisement);
      const fileUploaded = getValues(actionImposeesId);

      let currentFile;
      if (fileUploaded != null) {
        currentFile = { name: fileUploaded.name, status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD };
      } else {
        const actionsImposeesDatasetId = getActionImposeesDatasetId(actionImposeesId);
        if (actionsImposeesDatasetId != null || actionImposeesTableId != null) {
          currentFile = {
            name: labels.upload.fileName(actionImposeesId),
            status: UPLOAD_FILE_STATUS_KEY.READY_TO_DOWNLOAD,
          };
        } else currentFile = { name: '', status: UPLOAD_FILE_STATUS_KEY.EMPTY };
      }

      setCurrentActionsImposees({ ...currentActionsImposees, selectedGisement: gisement, currentFile });
    },
    [
      currentActionsImposees,
      forgeNewActionsImposeesId,
      getActionImposeesDatasetId,
      getActionsImposeesTableId,
      getValues,
      setCurrentActionsImposees,
    ]
  );

  useEffect(() => {
    if (currentActionsImposees.selectedGisement == null) selectGisement(defaultSelectedGisement);
    else selectGisement(currentActionsImposees.selectedGisement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = useCallback(
    (newValue) => {
      const parameterId = getActionImposeesId(currentActionsImposees.selectedGisement);
      setValue(
        parameterId,
        { ...newValue, gisement: currentActionsImposees.selectedGisement, tableType: 'SequencesActionsImposees' },
        { shouldDirty: true }
      );
    },
    [currentActionsImposees.selectedGisement, getActionImposeesId, setValue]
  );

  const checkRequiredColumnsInHeader = useCallback(
    (header) => {
      if (header == null) return null;

      const errors = [];

      requiredColumns.forEach((column) => {
        if (!header.includes(column)) errors.push(labels.fileErrors.missingColumn(column));
      });
      setFileError(errors);
      return errors;
    },
    [setFileError]
  );

  const handleUploadFile = useCallback(
    async (event) => {
      const file = event.target.files[0];
      event.target.value = null;
      if (file.type !== 'text/csv') {
        setFileError('Le fichier doit être en format .csv');
        setIsLoading(false);
        return;
      }
      // slice and read only the beginning of the file.
      const firstChunk = file.slice(0, 1024);
      const reader = new FileReader();
      reader.onload = (event) => {
        const splitChunk = event.target.result.trim().split(/\s*[\r\n]+\s*/g);
        const headerErrors = checkRequiredColumnsInHeader(splitChunk[0]);
        const fileToUpload = {
          name: file.name,
          status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
          file,
        };
        if (headerErrors.length === 0) {
          handleValueChange(fileToUpload);
        }
        setCurrentActionsImposees({
          ...currentActionsImposees,
          currentFile: fileToUpload,
        });
      };
      reader.readAsText(firstChunk);
    },
    [
      checkRequiredColumnsInHeader,
      currentActionsImposees,
      handleValueChange,
      setCurrentActionsImposees,
      setFileError,
      setIsLoading,
    ]
  );

  const handleDeleteFile = useCallback(() => {
    const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS.SequencesActionsImposees);
    const file = AgGridUtils.toCSV([], columns);
    const newFile = {
      name: labels.upload.fichierVide,
      status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
      file,
    };

    handleValueChange(newFile);
    setCurrentActionsImposees({ ...currentActionsImposees, currentFile: newFile });
  }, [currentActionsImposees, handleValueChange, setCurrentActionsImposees]);

  const handleDownload = useCallback(async () => {
    try {
      await downloadFileFromStorage(currentActionsImposees.selectedGisement);
    } catch (error) {
      console.error(error);
      setFileError("Le fichier associé n'existe pas");
    } finally {
      setIsLoading(false);
    }
  }, [downloadFileFromStorage, currentActionsImposees.selectedGisement, setFileError, setIsLoading]);

  return (
    <Grid container direction="row" justifyContent="center" alignItems="flex-start" sx={{ minHeight: 200 }}>
      <Grid item xs={4}>
        <Autocomplete
          id="gisement-selector"
          noOptionsText={labels.gisements.noOption}
          onChange={(event, newValue) => {
            selectGisement(newValue);
          }}
          value={currentActionsImposees.selectedGisement}
          size="small"
          disableClearable
          disabled
          options={gisements || []}
          renderOption={(props, option) => <li {...props}>{option}</li>}
          renderInput={(params) => (
            <TextField
              {...params}
              label={labels.gisements.label}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                'data-testid': `input-${labels.gisements.label.replace(/\s+/g, '-').toLowerCase()}`,
              }}
            />
          )}
        />
      </Grid>
      <Grid item>
        {isLoading ? (
          <CircularProgress data-cy="imposed-actions-loading-spinner" sx={{ mx: 2 }} />
        ) : (
          <Grid container sx={{ px: 4 }} direction="column" justifyContent="flex-end" alignItems="flex-start">
            <UploadFile
              acceptedFileTypes=".csv"
              key="imposed-actions"
              id="imposed-actions"
              labels={labels.upload}
              handleUploadFile={handleUploadFile}
              handleDeleteFile={handleDeleteFile}
              handleDownloadFile={handleDownload}
              file={currentActionsImposees.currentFile}
              editMode={editMode && currentActionsImposees.selectedGisement != null}
            />
            {getActionImposeesDatasetId(currentActionsImposees.selectedGisement) != null &&
              currentActionsImposees.currentFile.status !== UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD && (
                <Typography data-cy="source-changed" sx={{ ml: 16 }} variant="subtitle2">
                  {labels.upload.sourceModifiee}
                </Typography>
              )}
            {fileError && (
              <Grid item sx={{ width: 'auto', overflow: 'auto', p: 1 }}>
                {Array.isArray(fileError) ? (
                  <List data-cy="file-errors">
                    {fileError.map((error, index) => (
                      <li key={index}>
                        <Typography data-cy={`file-error-${index}`} variant="subtitle2" color="error">
                          {error}
                        </Typography>
                      </li>
                    ))}
                  </List>
                ) : (
                  <Typography data-cy={`file-error`} variant="subtitle2" color="error">
                    {fileError}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

ActionsImposeesTab.propTypes = {
  defaultSelectedGisement: PropTypes.string,
  gisements: PropTypes.array.isRequired,
  currentActionsImposees: PropTypes.object,
  setCurrentActionsImposees: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};
